import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIndividuals } from '../hooks/useIndividuals';
import { useDebouncedValue } from '../hooks/useDebounce';
import { formatDate, calculateAge } from '../utils/dateUtils';
import '../styles/ListPages.css';

import { Individual } from '../services/individualsService';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type SortColumn = 'clientID' | 'name' | 'age' | 'gender' | 'location' | 'intakeDate';
type SortDirection = 'asc' | 'desc';

export function ClientListPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDOB, setFilterDOB] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortColumn, setSortColumn] = useState<SortColumn>('clientID');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Reset to page 1 when page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(searchTerm, 500);

  // Use React Query hook with server-side pagination and search
  const { data, isLoading, isFetching, error } = useIndividuals({
    page,
    pageSize,
    searchQuery: debouncedSearch || undefined,
    isActive: true, // Only show active clients by default
  });

  const clients: Individual[] = data?.data || [];
  // Only block the whole page on the very first load (no data yet). Thanks to
  // keepPreviousData in useIndividuals, subsequent searches/page changes keep
  // the previous rows on screen (isLoading stays false), so the page no longer
  // blanks — and the search input keeps focus — on each keystroke.
  const loading = isLoading;
  // Background fetch in progress (search/page change) while prior rows remain visible.
  const isRefreshing = isFetching && !isLoading;
  // Gate the "no clients match" empty-state while a search is still in
  // flight (background-fetching or the debounce hasn't caught up yet) so we
  // never flash "not found" for a query that hasn't resolved yet.
  const isSearching = isFetching || debouncedSearch !== searchTerm;

  // Handle column sort click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort indicator component
  const SortIndicator = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <span className="sort-indicator sort-inactive">⇅</span>;
    }
    return (
      <span className="sort-indicator sort-active">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Client-side filtering (DOB only - search is handled server-side) and sorting
  const displayClients = useMemo(
    () => {
      // Filter by DOB if specified (server doesn't support DOB filter)
      let result = filterDOB
        ? clients.filter((client: Individual) => client.dateOfBirth === filterDOB)
        : clients;

      // Sort the current page results
      result = [...result].sort((a: Individual, b: Individual) => {
        let comparison = 0;

        switch (sortColumn) {
          case 'clientID':
            comparison = (a.clientID || '').localeCompare(b.clientID || '');
            break;
          case 'name': {
            const nameA = `${a.lastName}, ${a.firstName}`.toLowerCase();
            const nameB = `${b.lastName}, ${b.firstName}`.toLowerCase();
            comparison = nameA.localeCompare(nameB);
            break;
          }
          case 'age': {
            const ageA = a.dateOfBirth ? calculateAge(a.dateOfBirth) : 0;
            const ageB = b.dateOfBirth ? calculateAge(b.dateOfBirth) : 0;
            comparison = ageA - ageB;
            break;
          }
          case 'gender':
            comparison = (a.gender || '').localeCompare(b.gender || '');
            break;
          case 'location': {
            const locA = `${a.city}, ${a.zipCode}`.toLowerCase();
            const locB = `${b.city}, ${b.zipCode}`.toLowerCase();
            comparison = locA.localeCompare(locB);
            break;
          }
          case 'intakeDate': {
            const dateA = a.intakeDate ? new Date(a.intakeDate).getTime() : 0;
            const dateB = b.intakeDate ? new Date(b.intakeDate).getTime() : 0;
            comparison = dateA - dateB;
            break;
          }
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });

      return result;
    },
    [clients, filterDOB, sortColumn, sortDirection]
  );

  // Use server-side pagination metadata, fallback for DOB filter (client-side)
  const serverPagination = data?.pagination;
  const totalItems = filterDOB ? displayClients.length : (serverPagination?.totalItems ?? clients.length);
  const totalPages = filterDOB
    ? Math.max(1, Math.ceil(displayClients.length / pageSize))
    : (serverPagination?.totalPages ?? Math.max(1, Math.ceil(clients.length / pageSize)));

  // When DOB filter is active, we need client-side pagination; otherwise server handles it
  const paginatedClients = filterDOB
    ? displayClients.slice((page - 1) * pageSize, page * pageSize)
    : displayClients;

  // Reset to page 1 when search term changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page on new search
  };

  if (loading) {
    return <div className="loading">Loading clients...</div>;
  }

  return (
    <div className="list-page">
      <div className="page-header">
        <h2>Client Management</h2>
        <p>View and manage all clients in the system</p>
      </div>

      <div className="list-actions">
        <Link to="/clients/intake">
          <button className="button-primary">Add New Client</button>
        </Link>
        <Link to="/clients/log-visit">
          <button className="button-secondary">Log Service Visit</button>
        </Link>
      </div>

      {error && (
        <div className="alert alert-error">
          {error instanceof Error ? error.message : 'Failed to load clients'}
        </div>
      )}

      <div className="filters-card">
        <h3>Search & Filter</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Name, Client ID, Email, or Phone..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="filterDOB">Date of Birth</label>
            <input
              type="date"
              id="filterDOB"
              value={filterDOB}
              onChange={(e) => setFilterDOB(e.target.value)}
            />
          </div>

          {(searchTerm || filterDOB) && (
            <div className="filter-group">
              <label>&nbsp;</label>
              <button
                className="button-secondary"
                onClick={() => {
                  handleSearchChange('');
                  setFilterDOB('');
                }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="results-count">
          Showing {paginatedClients.length} of {totalItems} clients
          {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
          {isRefreshing && <span style={{ marginLeft: '0.5rem', color: '#888' }}>· searching…</span>}
        </div>
      </div>

      {paginatedClients.length === 0 && isSearching ? (
        <div className="card">
          <p style={{ textAlign: 'center', padding: '2rem' }}>Searching…</p>
        </div>
      ) : paginatedClients.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', padding: '2rem' }}>
            {clients.length === 0
              ? 'No clients found. Add your first client to get started.'
              : 'No clients match your search criteria.'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => handleSort('clientID')}>
                    Client ID <SortIndicator column="clientID" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('name')}>
                    Name <SortIndicator column="name" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('age')}>
                    Age <SortIndicator column="age" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('gender')}>
                    Gender <SortIndicator column="gender" />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('location')}>
                    Location <SortIndicator column="location" />
                  </th>
                  <th>Contact</th>
                  <th className="sortable-header" onClick={() => handleSort('intakeDate')}>
                    Intake Date <SortIndicator column="intakeDate" />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((client: Individual) => (
                  <tr key={client.individualID}>
                    <td className="client-id">{client.clientID}</td>
                    <td className="client-name">
                      <strong>{client.lastName}, {client.firstName}</strong>
                    </td>
                    <td>{calculateAge(client.dateOfBirth)}</td>
                    <td>{client.gender}</td>
                    <td>{client.city}, {client.zipCode}</td>
                    <td className="contact-info">
                      <div>{client.phoneNumber}</div>
                      {client.emailAddress && (
                        <div className="email-small">{client.emailAddress}</div>
                      )}
                    </td>
                    <td>{client.intakeDate ? formatDate(client.intakeDate) : ''}</td>
                    <td className="actions">
                      <button
                        className="button-link"
                        onClick={() => navigate(`/clients/${client.clientID}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Compact */}
          <div className="mobile-cards">
            {paginatedClients.map((client: Individual) => (
              <div
                key={client.individualID}
                className="mobile-card-compact"
                onClick={() => navigate(`/clients/${client.clientID}`)}
              >
                <div className="mobile-card-main">
                  <div className="mobile-card-name">
                    {client.lastName}, {client.firstName}
                  </div>
                  <div className="mobile-card-info">
                    <span>{calculateAge(client.dateOfBirth)} • {client.gender}</span>
                    <span className="mobile-card-location">{client.city}, {client.zipCode}</span>
                  </div>
                </div>
                <div className="mobile-card-right">
                  <span className="mobile-card-id">{client.clientID}</span>
                  {client.phoneNumber && (
                    <a
                      href={`tel:${client.phoneNumber}`}
                      className="mobile-card-phone"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📞
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '1rem', 
            marginTop: '1.5rem',
            padding: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label htmlFor="pageSize" style={{ fontSize: '0.9rem', color: '#666' }}>
                Show:
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '0.9rem'
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <>
                <button
                  className="button-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  Previous
                </button>
                
                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  Page {page} of {totalPages}
                </span>
                
                <button
                  className="button-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
